'use client';

import { Box } from '@mui/material';
import styled from '@emotion/styled';
import Image from 'next/image';
import { color, borderRadius, spacing } from '@/theme/tokens';
import { MDXProvider } from '@mdx-js/react';
import { usePathname } from 'next/navigation';
import { breakpoints } from '@/theme/tokens';
import { getMDXComponents } from '@/components/common/MDXComponents';

const Wrapper = styled(Box)`
    padding: ${spacing[16]};
    background-color: ${color.white[0]};
    border-radius: ${borderRadius[8]};
    display: flex;
    flex-direction: column;

    @media (min-width: ${breakpoints.sm}) {
        padding: ${spacing[24]};
    }

    @media (min-width: ${breakpoints.md}) {
        padding: ${spacing[32]};
    }
`;

const ServiceImage = styled(Box)`
    position: relative;
    width: 100%;
    aspect-ratio: 1992/996;
    overflow: hidden;
    margin: ${spacing[16]} 0;

    @media (min-width: ${breakpoints.sm}) {
        margin: ${spacing[24]} 0;
    }

    @media (min-width: ${breakpoints.md}) {
        margin: ${spacing[32]} 0;
    }
`;

const getServiceImage = (pathname: string) => {
    if (pathname.includes('/services/auction-access')) {
        return '/services/services-auction-access.jpg';
    } else if (pathname.includes('/services/domestic-transportation')) {
        return '/services/services-domestic-transportation.jpg';
    } else if (pathname.includes('/services/global-shipping')) {
        return '/services/services-global-shipping.jpg';
    } else if (pathname.includes('/services/faq')) {
        return null;
    } else {
        return '/services/services-general-info.jpg';
    }
};

export default function ServiceContent({ children }: { children: React.ReactNode }) {
    const pathname = usePathname();
    const serviceImage = getServiceImage(pathname);

    const h1Image = serviceImage ? (
        <ServiceImage>
            <Image
                src={serviceImage}
                alt="Service illustration"
                fill
                priority
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px"
                style={{
                    objectFit: 'cover',
                    objectPosition: '50% 50%',
                }}
            />
        </ServiceImage>
    ) : null;

    return (
        <Wrapper>
            <MDXProvider
                components={getMDXComponents({
                    customLink: true,
                    h1Image,
                    paragraphMargin: {
                        base: spacing[16],
                        sm: spacing[20],
                        md: spacing[24]
                    },
                    listMargin: {
                        base: spacing[16],
                        sm: spacing[20],
                        md: spacing[24]
                    }
                })}
            >
                {children}
            </MDXProvider>
        </Wrapper>
    );
}
